import { LogOut, Bell, Eye, EyeOff } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminHeader = ({ user, token, onLogout, reveal, onToggleReveal }) => {
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [readIds, setReadIds] = useState(new Set());

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 10000);
      return () => clearInterval(interval);
    }
  }, [token, fetchNotifications]);

  const unreadCount = notifications.filter(n => !readIds.has(n.id)).length;

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReadIds(prev => new Set([...prev, id]));
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "сейчас";
    if (diffMins < 60) return `${diffMins} мин`;
    const diffHours = Math.floor(diffMs / 3600000);
    if (diffHours < 24) return `${diffHours} ч`;
    return date.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" });
  };

  return (
    <header className="border-b border-[#1F1F1F] bg-[#000000] sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center gap-2">
            <span className="text-[#EDEDED] font-chivo text-sm">ADMIN</span>
            <h1 className="font-chivo font-bold text-xl text-white">Web-<span className="text-[#EDEDED]">3</span></h1>
          </div>
          <div className="flex items-center gap-4">
            {/* Reveal hidden clients toggle */}
            {onToggleReveal && (
              <button
                onClick={onToggleReveal}
                data-testid="reveal-hidden-btn"
                title={reveal ? "Скрыть скрытых клиентов" : "Показать скрытых клиентов (по ключу)"}
                className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm transition-colors ${
                  reveal ? "bg-[#FF8A1E]/20 text-[#FF8A1E]" : "text-[#A1A1AA] hover:text-white"
                }`}
              >
                {reveal ? <EyeOff size={18} /> : <Eye size={18} />}
                <span className="hidden sm:inline">{reveal ? "Скрыть скрытых" : "Показать скрытых"}</span>
              </button>
            )}
            {/* Notifications Bell */}
            <div className="relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="p-2 text-[#A1A1AA] hover:text-white transition-colors relative"
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#F87171] rounded-full text-white text-xs flex items-center justify-center font-bold">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
              
              {showDropdown && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setShowDropdown(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-96 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg shadow-xl z-50 max-h-[500px] overflow-hidden">
                    <div className="p-3 border-b border-[#1F1F1F] flex justify-between items-center">
                      <span className="font-semibold text-white">Уведомления</span>
                      {unreadCount > 0 && (
                        <span className="text-xs text-[#EDEDED]">{unreadCount} новых</span>
                      )}
                    </div>
                    <div className="max-h-[400px] overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="p-4 text-center text-[#A1A1AA] text-sm">
                          Нет уведомлений
                        </div>
                      ) : (
                        notifications.slice(0, 20).map((notif) => {
                          const isRead = readIds.has(notif.id);
                          return (
                            <div
                              key={notif.id}
                              onClick={() => !isRead && markAsRead(notif.id)}
                              className={`p-3 border-b border-[#1F1F1F]/50 cursor-pointer transition-colors ${
                                isRead ? 'opacity-60' : 'hover:bg-[#1A1A1A]'
                              }`}
                            >
                              <div className="flex items-start gap-2">
                                {!isRead && (
                                  <span className="w-2 h-2 bg-white rounded-full mt-1.5 flex-shrink-0"></span>
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex justify-between items-start gap-2">
                                    <span className="font-medium text-white text-sm">{notif.title}</span>
                                    <span className="text-[#A1A1AA] text-xs whitespace-nowrap">{formatTime(notif.created_at)}</span>
                                  </div>
                                  <p className="text-[#A1A1AA] text-xs mt-0.5">{notif.message}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            
            <span className="text-[#A1A1AA]">{user?.email}</span>
            <button
              onClick={onLogout}
              className="text-[#A1A1AA] hover:text-[#F87171] transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;
